import { emptyPreflightApi as api } from './empty-api';
export const addTagTypes = [
  'Event Resource',
  'User List Resource',
  'Post Resource',
  'Survey Resource'
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getApiEventsByIdAttendees: build.query<
        GetApiEventsByIdAttendeesApiResponse,
        GetApiEventsByIdAttendeesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/events/${queryArg.id}/attendees`,
          params: {
            accessAttributeFilter: queryArg.accessAttributeFilter,
            page: queryArg.page,
            search: queryArg.search,
            size: queryArg.size,
            sort: queryArg.sort
          }
        }),
        providesTags: ['Event Resource']
      }),
      postApiLists: build.query<PostApiListsApiResponse, PostApiListsApiArg>({
        query: (queryArg) => ({
          url: `/api/lists`,
          method: 'POST',
          body: queryArg.createUserList
        }),
        providesTags: ['User List Resource']
      }),
      patchApiListsById: build.query<
        PatchApiListsByIdApiResponse,
        PatchApiListsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/lists/${queryArg.id}`,
          method: 'PATCH',
          body: queryArg.updateUserListInfo
        }),
        providesTags: ['User List Resource']
      }),
      deleteApiListsById: build.query<
        DeleteApiListsByIdApiResponse,
        DeleteApiListsByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/lists/${queryArg.id}`,
          method: 'DELETE'
        }),
        providesTags: ['User List Resource']
      }),
      postApiPostsByIdPublish: build.query<
        PostApiPostsByIdPublishApiResponse,
        PostApiPostsByIdPublishApiArg
      >({
        query: (queryArg) => ({
          url: `/api/posts/${queryArg.id}/publish`,
          method: 'POST'
        }),
        providesTags: ['Post Resource']
      }),
      getApiPostsByIdRecipients: build.query<
        GetApiPostsByIdRecipientsApiResponse,
        GetApiPostsByIdRecipientsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/posts/${queryArg.id}/recipients`,
          params: {
            accessAttributeFilter: queryArg.accessAttributeFilter,
            emailFilter: queryArg.emailFilter,
            page: queryArg.page,
            search: queryArg.search,
            size: queryArg.size,
            sort: queryArg.sort
          }
        }),
        providesTags: ['Post Resource']
      }),
      getApiSurveysByIdAllResponses: build.query<
        GetApiSurveysByIdAllResponsesApiResponse,
        GetApiSurveysByIdAllResponsesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/surveys/${queryArg.id}/all-responses`,
          params: {
            accessAttributeFilter: queryArg.accessAttributeFilter,
            search: queryArg.search,
            sort: queryArg.sort
          }
        }),
        providesTags: ['Survey Resource']
      }),
      getApiSurveysByIdParticipants: build.query<
        GetApiSurveysByIdParticipantsApiResponse,
        GetApiSurveysByIdParticipantsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/surveys/${queryArg.id}/participants`,
          params: {
            accessAttributeFilter: queryArg.accessAttributeFilter,
            page: queryArg.page,
            search: queryArg.search,
            size: queryArg.size,
            sort: queryArg.sort
          }
        }),
        providesTags: ['Survey Resource']
      })
    }),
    overrideExisting: false
  });
export { injectedRtkApi as preflightApi };
export type GetApiEventsByIdAttendeesApiResponse =
  /** status 200 OK */ PaginatedResponseAttendResponseRead;
export type GetApiEventsByIdAttendeesApiArg = {
  id: ResourceId;
  accessAttributeFilter?: AccessAttributeFilter | null;
  page?: number | null;
  search?: string | null;
  size?: number | null;
  sort?: string;
};
export type PostApiListsApiResponse = /** status 200 OK */ any;
export type PostApiListsApiArg = {
  createUserList: CreateUserList;
};
export type PatchApiListsByIdApiResponse = unknown;
export type PatchApiListsByIdApiArg = {
  id: ResourceId;
  updateUserListInfo: UpdateUserListInfo;
};
export type DeleteApiListsByIdApiResponse = unknown;
export type DeleteApiListsByIdApiArg = {
  id: ResourceId;
};
export type PostApiPostsByIdPublishApiResponse = /** status 200 OK */ any;
export type PostApiPostsByIdPublishApiArg = {
  id: ResourceId;
};
export type GetApiPostsByIdRecipientsApiResponse =
  /** status 200 OK */ PaginatedResponsePostRecipient;
export type GetApiPostsByIdRecipientsApiArg = {
  id: ResourceId;
  accessAttributeFilter?: AccessAttributeFilter | null;
  emailFilter?: UserEmailFilter & any;
  page?: number | null;
  search?: string | null;
  size?: number | null;
  sort?: string[] | null;
};
export type GetApiSurveysByIdAllResponsesApiResponse =
  /** status 200 OK */ SurveyResponseList;
export type GetApiSurveysByIdAllResponsesApiArg = {
  id: ResourceId;
  accessAttributeFilter?: AccessAttributeFilter | null;
  search?: string | null;
  sort?: string[];
};
export type GetApiSurveysByIdParticipantsApiResponse =
  /** status 200 OK */ PaginatedResponseSurveyParticipant;
export type GetApiSurveysByIdParticipantsApiArg = {
  id: ResourceId;
  accessAttributeFilter?: AccessAttributeFilter | null;
  page?: number | null;
  search?: string | null;
  size?: number | null;
  sort?: string[];
};
export type Id = string | number;
export type Gender = 'FEMALE' | 'MALE' | 'DIVERSE' | 'UNKNOWN';
export type Group = {
  id: Id;
  name: string;
  logo?: string | null;
  parentId?: number | null;
};
export type GroupMembershipType = {
  id: Id;
  name: string;
};
export type GroupWithMembership = {
  group: Group;
  membership: GroupMembershipType;
};
export type CompanySummary = {
  id: Id;
  name: string | null;
  city: string | null;
  postalCode: string | null;
  street?: string | null;
  phone?: string | null;
  fax?: string | null;
  groups?: GroupWithMembership[] | null;
};
export type EmailUser = {
  id: Id;
  firstName?: string | null;
  lastName?: string | null;
  gender?: Gender | null;
  email?: string | null;
  companies?: CompanySummary[] | null;
  isExternal?: boolean | null;
  isCompanyUser?: boolean | null;
  emailBlocked?: boolean | null;
  emailValid?: boolean;
};
export type AttendeeStatus =
  | 'ATTENDING'
  | 'NOT_ATTENDING'
  | 'MAYBE_ATTENDING'
  | 'NO_ANSWER'
  | 'NOT_INVITED'
  | 'ATTENDED'
  | 'WAIT_LISTED';
export type Instant = string;
export type SurveyPrivacy = 'ANONYMOUS' | 'IDENTIFIED';
export type Survey = {
  id: Id;
  title?: string | null;
  json: string;
  responseDeadline: Instant;
  createdAt: Instant;
  isTemplate?: boolean;
  description?: string | null;
  privacy: SurveyPrivacy;
  publishedAt?: Instant | null;
};
export type SurveyRead = {
  id: Id;
  title?: string | null;
  json: string;
  responseDeadline: Instant;
  createdAt: Instant;
  creator?: EmailUser | null;
  isTemplate?: boolean;
  description?: string | null;
  privacy: SurveyPrivacy;
  publishedAt?: Instant | null;
};
export type SurveyResponse = {
  id: number;
  user: EmailUser | null;
  json: string;
  createdAt: Instant;
  updatedAt: Instant;
  publishedAt: Instant | null;
  privacy?: SurveyPrivacy;
};
export type SurveyWithResponse = {
  survey: Survey;
  response?: SurveyResponse | null;
  token: string;
};
export type SurveyWithResponseRead = {
  survey: SurveyRead;
  response?: SurveyResponse | null;
  token: string;
};
export type AttendResponse = {
  user: EmailUser;
  status: AttendeeStatus;
  surveyWithResponse?: SurveyWithResponse | null;
};
export type AttendResponseRead = {
  user: EmailUser;
  status: AttendeeStatus;
  surveyWithResponse?: SurveyWithResponseRead | null;
};
export type PaginationMetadata = {
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  next?: string | null;
  previous?: string | null;
};
export type PaginatedResponseAttendResponse = {
  data: AttendResponse[];
  meta: PaginationMetadata;
};
export type PaginatedResponseAttendResponseRead = {
  data: AttendResponseRead[];
  meta: PaginationMetadata;
};
export type Responsible = 'USER' | 'AUTHOR' | 'TENANT_ADMIN' | 'PLATFORM';
export type ErrorBody = {
  /** Stable i18n key the client should use to look up the localized template in /i18n/errors.<locale>.json. */
  key: string;
  /** English fallback **template** with `{name}` placeholders. Identical to the entry under `key` in /i18n/errors.en.json — provided in the response so clients without the EN file can still render. Clients should ALWAYS substitute `messageArgs` into this (or into the locale-specific template) — never display it directly. */
  message: string;
  /** HTTP status code, echoed for convenience. */
  status: number;
  /** Runtime variables matching the `{name}` placeholders in `message` (and in the locale-specific template at /i18n/errors.<locale>.json). Empty for messages that have no variables. */
  messageArgs?: {
    [key: string]: string;
  };
  /** Who configured the constraint that produced this error. Use this to route the user to the right remediation path (TENANT_ADMIN → contact admin, AUTHOR → contact author, PLATFORM → contact support, USER → fix your own input). */
  responsible: Responsible;
};
export type Violation = {
  field: string;
  message: string;
};
export type ViolationReport = {
  title: string;
  status: number;
  key: string;
  violations: Violation[];
};
export type ResourceId = string | number;
export type AccessAttributeFilter = object;
export type CreateAttributeValueByName = {
  attributeName: string;
  value: string;
};
export type Recipients = {
  accessAttributeValueIds?: number[];
  accessAttributes?: CreateAttributeValueByName[];
};
export type CreateUserList = {
  name: string;
  description?: string | null;
  recipients: Recipients;
  ephemeral?: boolean;
  signatureId?: ResourceId | null;
  templateIds?:
    | {
        [key: string]: ResourceId;
      }
    | {
        [key: string]: ResourceId;
      };
  chatEnabled?: boolean;
};
export type UpdateUserListInfo = {
  name?: string | null;
  description?: string | null;
  recipients?: {
    accessAttributeValueIds?: number[];
    accessAttributes?: CreateAttributeValueByName[];
  } | null;
  signatureId?: number | string | null;
  chatEnabled?: boolean | null;
  templateIds?: number | string | null;
};
export type LocalDateTime = string;
export type PostNotificationError = {
  type: string;
  message: string;
  description?: string | null;
  createdAt: LocalDateTime;
  postNotificationId?: number | null;
};
export type PostNotification = {
  id?: number;
  userId?: number;
  type: string;
  queuedAt: LocalDateTime;
  sentAt?: LocalDateTime | null;
  receivedAt?: LocalDateTime | null;
  readAt?: LocalDateTime | null;
  errors?: PostNotificationError[];
  failedAt?: LocalDateTime | null;
  sendError?: string | null;
};
export type NotificationSettings = {
  tagId?: number | null;
  email: boolean;
  cloudMessage: boolean;
  fax: boolean;
  postal: boolean;
};
export type PostRecipient = {
  user: EmailUser;
  notifications?: PostNotification[];
  notificationSettings?: NotificationSettings | null;
  policies?: string[];
};
export type PaginatedResponsePostRecipient = {
  data: PostRecipient[];
  meta: PaginationMetadata;
};
export type UserEmailFilter =
  'ALL' | 'HAS_EMAIL' | 'HAS_NO_EMAIL' | 'EMAIL_BLOCKED';
export type SurveyResponseList = {
  items: SurveyResponse[];
  totalRecipients: number;
};
export type SurveyParticipant = {
  user: EmailUser;
  response?: SurveyResponse | null;
};
export type PaginatedResponseSurveyParticipant = {
  data: SurveyParticipant[];
  meta: PaginationMetadata;
};
export const {
  useGetApiEventsByIdAttendeesQuery,
  usePostApiListsQuery,
  usePatchApiListsByIdQuery,
  useDeleteApiListsByIdQuery,
  usePostApiPostsByIdPublishQuery,
  useGetApiPostsByIdRecipientsQuery,
  useGetApiSurveysByIdAllResponsesQuery,
  useGetApiSurveysByIdParticipantsQuery
} = injectedRtkApi;
